import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search, Visibility, Download } from '@mui/icons-material';
import { useDebouncedCallback } from 'use-debounce';
import {
  createEezyguardClient,
  EezyguardScope,
  OccurrenceFilters,
  OccurrenceListItem,
  STATUS_LABELS,
  SEVERITY_LABELS,
} from 'src/apis/eezyguard';
import { GetCompanies } from 'src/apis/company';
import SkeletonLoad from 'src/pages/components/SkeletonLoad';
import NoDataAlert from 'src/pages/components/NoDataAlert';
import { PriorityChip, SeverityChip, StatusChip } from './common';
import { EEZYGUARD_TYPE_LABELS } from './labels';

const ROWS_PER_PAGE = 10;

type CompanyOption = { id: number; trade_name: string };

type Props = {
  scope: EezyguardScope;
  onError?: (message: string) => void;
};

export default function EezyGuardOccurrenceList({ scope, onError }: Props): JSX.Element {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const client = useMemo(() => createEezyguardClient(scope), [scope]);
  const isAdmin = scope === 'admin';

  const [items, setItems] = useState<OccurrenceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [protocol, setProtocol] = useState('');
  const [filters, setFilters] = useState<OccurrenceFilters>({});
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    GetCompanies()
      .then((res) => {
        const data = (res?.data ?? []) as CompanyOption[];
        setCompanies(Array.isArray(data) ? data : []);
      })
      .catch(() => setCompanies([]));
  }, [isAdmin]);

  const fetchData = useCallback(
    (currentPage: number, currentFilters: OccurrenceFilters, protocolValue: string) => {
      setLoading(true);
      client
        .listOccurrences({
          ...currentFilters,
          protocol: protocolValue || undefined,
          page: currentPage + 1,
          page_size: ROWS_PER_PAGE,
        })
        .then((res) => {
          setItems(Array.isArray(res.items) ? res.items : []);
          setTotal(res.total ?? 0);
        })
        .catch(() => onError?.('Erro ao carregar ocorrências.'))
        .finally(() => setLoading(false));
    },
    [client, onError]
  );

  useEffect(() => {
    fetchData(page, filters, protocol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, JSON.stringify(filters)]);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setPage(0);
    fetchData(0, filters, value);
  }, 400);

  const detailPath = (id: number) =>
    isAdmin ? `/eezyguard/ocorrencias/${id}` : `/eezyguard/empresa/ocorrencias/${id}`;

  const setFilter = (key: keyof OccurrenceFilters, value: string) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const exportHref = `${process.env.REACT_APP_BACKEND_URL ?? ''}${client.exportUrl({
    ...filters,
    protocol: protocol || undefined,
  })}`;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por protocolo"
            value={protocol}
            onChange={(e) => {
              setProtocol(e.target.value);
              debouncedSearch(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isAdmin && (
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Empresa"
              value={filters.company_id ?? ''}
              onChange={(e) => setFilter('company_id', e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.trade_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={filters.status ?? ''}
            onChange={(e) => setFilter('status', e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Gravidade"
            value={filters.severity ?? ''}
            onChange={(e) => setFilter('severity', e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(SEVERITY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Tipo"
            value={filters.occurrence_type ?? ''}
            onChange={(e) => setFilter('occurrence_type', e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(EEZYGUARD_TYPE_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end">
        <Button
          size="small"
          startIcon={<Download />}
          component="a"
          href={exportHref}
          target="_blank"
          rel="noreferrer"
        >
          Exportar Excel
        </Button>
      </Box>

      {loading ? (
        <SkeletonLoad variant="table" rows={ROWS_PER_PAGE} columns={7} />
      ) : items.length === 0 ? (
        <NoDataAlert />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Protocolo</TableCell>
                {isAdmin && <TableCell>Empresa</TableCell>}
                <TableCell>Tipo</TableCell>
                {!isMobile && <TableCell>Setor</TableCell>}
                <TableCell>Gravidade</TableCell>
                <TableCell>Prioridade</TableCell>
                <TableCell>Status</TableCell>
                {!isAdmin && !isMobile && <TableCell>Encaminhada</TableCell>}
                {!isMobile && <TableCell>Criada em</TableCell>}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.protocol}</TableCell>
                  {isAdmin && (
                    <TableCell>{item.company_name ?? item.company_id}</TableCell>
                  )}
                  <TableCell>
                    {EEZYGUARD_TYPE_LABELS[item.occurrence_type] ??
                      item.occurrence_type}
                  </TableCell>
                  {!isMobile && <TableCell>{item.sector ?? '—'}</TableCell>}
                  <TableCell>
                    <SeverityChip severity={item.severity} />
                  </TableCell>
                  <TableCell>
                    <PriorityChip priority={item.priority} />
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                  </TableCell>
                  {!isAdmin && !isMobile && (
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.forwarded_to_eezycare ? 'Sim' : 'Não'}
                        color={item.forwarded_to_eezycare ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {new Date(item.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Tooltip title="Ver detalhes">
                      <IconButton
                        size="small"
                        onClick={() => navigate(detailPath(item.id))}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}
    </Box>
  );
}
